import React, { useState, useEffect } from 'react';
import { getTranslations } from '../../../../../../services/languageUtils';
import ModalBase from './modalBase';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import KeysManagementService from '../../../../../../services/Gowd/keysManagementService';

/**
 * Criação de chave PIX.
 *
 * ── O que mudou, e por quê ──
 *
 * Eram TRÊS botões — "e-mail", "telefone", "documento" — e o tipo era escolhido clicando num
 * deles. Quem digitava a chave tinha de dizer de novo, em outro lugar, o que ela era; e um
 * clique no botão errado criava a chave com o tipo errado, sem nada barrar.
 *
 * Agora o tipo é DEDUZIDO do que foi digitado, e continua editável: a dedução acerta o caso
 * comum sem impedir o incomum (um telefone digitado sem DDI, por exemplo). Um botão só,
 * porque criar é uma ação, não três.
 *
 * ── Telefone e e-mail passam por confirmação ──
 *
 * Não são documentos: o Banco Central exige provar a posse antes de vincular a chave. A
 * criação devolve `validationId` e manda um código para o contato; a chave só passa a existir
 * depois de confirmada. Fechar o modal na criação faria a tela dizer "pronto" para uma chave
 * que nunca ficou pronta.
 */

const TIPOS = [
  { value: 'EVP', label: 'Aleatória' },
  { value: 'CPF', label: 'CPF' },
  { value: 'CNPJ', label: 'CNPJ' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'PHONE', label: 'Telefone' },
];

/** Tipos em que o Banco Central exige confirmar a posse do contato. */
const PRECISA_CONFIRMAR = ['EMAIL', 'PHONE'];

/**
 * Deduz o tipo pelo formato do que foi digitado.
 *
 * A ordem importa: o e-mail é reconhecido pela arroba antes de qualquer contagem de dígitos,
 * senão um e-mail com muitos números cairia em documento. Vazio devolve `EVP`, que é a chave
 * que o banco gera e a única que não precisa de valor.
 *
 * Devolve `null` quando NÃO reconhece — e aí a tela não sobrescreve o que a pessoa escolheu à
 * mão. Chutar um tipo para um valor que não casa com nada é como se cria chave com o tipo
 * errado sem ninguém notar.
 */
export function deduzirTipoDeChave(valor) {
  const texto = (valor || '').trim();

  if (!texto) return 'EVP';
  if (texto.includes('@')) return 'EMAIL';

  const digitos = texto.replace(/\D/g, '');

  // Telefone brasileiro em E.164: +55 mais DDD e número (12 ou 13 dígitos com o 55).
  if (texto.startsWith('+') || /^55\d{10,11}$/.test(digitos)) {
    return digitos.length >= 12 && digitos.length <= 13 ? 'PHONE' : null;
  }

  if (digitos.length === 11 && digitos === texto.replace(/[.\-\s]/g, '')) return 'CPF';
  if (digitos.length === 14) return 'CNPJ';

  // Chave aleatória já existente tem formato de UUID.
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(texto)) return 'EVP';

  return null;
}

function Modal({ show, onClose, updateKeys }) {
  const [translations, setTranslations] = useState({});

  const [valor, setValor] = useState('');
  const [tipo, setTipo] = useState('EVP');
  /* Verdadeiro depois de a pessoa escolher o tipo à mão: a dedução para de sobrescrever. */
  const [tipoManual, setTipoManual] = useState(false);
  const [canal, setCanal] = useState('sms');

  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  /* Confirmação pendente. Enquanto existe, o modal pede o código em vez de criar outra chave. */
  const [pendente, setPendente] = useState(null);
  const [codigo, setCodigo] = useState('');

  useEffect(() => {
    getTranslations(localStorage.getItem('language') || 'pt').then(setTranslations);
  }, []);

  // Reabrir o modal começa limpo: um valor deixado da tentativa anterior seria criado sem
  // ninguém reparar.
  useEffect(() => {
    if (show) {
      setValor('');
      setTipo('EVP');
      setTipoManual(false);
      setCanal('sms');
      setErro('');
      setPendente(null);
      setCodigo('');
    }
  }, [show]);

  function mudarValor(texto) {
    setValor(texto);
    setErro('');

    if (tipoManual) return;

    const deduzido = deduzirTipoDeChave(texto);
    if (deduzido) setTipo(deduzido);
  }

  const aleatoria = tipo === 'EVP';
  const podeCriar = !enviando && (aleatoria || valor.trim().length > 0);

  async function criar() {
    setErro('');
    setEnviando(true);

    try {
      const res = await KeysManagementService.create({
        type: tipo,
        key: aleatoria ? '' : valor.trim(),
        channel: tipo === 'PHONE' ? canal : undefined,
      });

      const data = res?.data?.data ?? res?.data;

      /*
        O que decide se falta confirmar é o `validationId`, não o status: é ele que a
        confirmação consome. Depender do status faria o modal pedir código quando o provedor
        devolvesse PENDING por outro motivo.
      */
      const validationId = data?.validationId || data?.ValidationId;

      if (validationId) {
        setPendente({ validationId });
        return;
      }

      toast(translations.success || 'Chave criada.', { type: 'success', autoClose: 2500, theme: 'light' });
      updateKeys();
      onClose();
    } catch (err) {
      console.error(err);
      // A frase vem do servidor e nomeia o problema (chave já registrada, tipo inválido,
      // canal em chave que não é telefone). Um texto genérico deixaria a pessoa repetindo.
      setErro(
        err?.response?.data?.message ||
          translations.erroInProccessRequest ||
          'Não foi possível criar a chave.',
      );
    } finally {
      setEnviando(false);
    }
  }

  async function confirmar() {
    setErro('');
    setEnviando(true);

    try {
      await KeysManagementService.confirm({
        validationId: pendente.validationId,
        code: codigo.trim(),
      });

      toast(translations.success || 'Chave confirmada.', { type: 'success', autoClose: 2500, theme: 'light' });
      updateKeys();
      onClose();
    } catch (err) {
      console.error(err);
      setErro(
        err?.response?.data?.message ||
          'Código não aceito. Confira e tente de novo — ele expira em alguns minutos.',
      );
    } finally {
      setEnviando(false);
    }
  }

  const rotuloContato = tipo === 'PHONE' ? (canal === 'whatsapp' ? 'WhatsApp' : 'SMS') : 'e-mail';

  return (
    <ModalBase show={show} onClose={onClose}>
      <div style={{ width: 'min(92vw, 420px)', color: '#101828' }}>
        {!pendente ? (
          <>
            <h2 className="text-xl mb-1 text-center font-bold">
              {translations.new_pix_key || 'Nova chave PIX'}
            </h2>
            <p style={{ textAlign: 'center', fontSize: 12, color: '#667085', marginBottom: 16 }}>
              Digite a chave — o tipo é identificado automaticamente.
            </p>

            <label style={{ fontSize: 12, fontWeight: 'bold', color: '#667085' }}>
              {aleatoria ? 'Chave' : 'Chave'}
            </label>
            <input
              type="text"
              value={valor}
              onChange={(e) => mudarValor(e.target.value)}
              disabled={aleatoria}
              placeholder={
                aleatoria
                  ? 'Gerada pelo banco — não precisa digitar'
                  : 'CPF, CNPJ, e-mail ou +55…'
              }
              className="w-full px-4 py-2 rounded"
              style={{
                color: '#101828',
                border: '1px solid #cccccc',
                marginTop: 4,
                background: aleatoria ? '#f2f4f7' : '#ffffff',
              }}
            />

            <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 150px' }}>
                <label style={{ fontSize: 12, fontWeight: 'bold', color: '#667085' }}>Tipo</label>
                <select
                  value={tipo}
                  onChange={(e) => {
                    // Escolher à mão desliga a dedução: quem sabe o tipo tem a palavra final.
                    setTipo(e.target.value);
                    setTipoManual(true);
                    setErro('');
                  }}
                  className="w-full px-4 py-2 rounded"
                  style={{ color: '#101828', border: '1px solid #cccccc', marginTop: 4 }}
                >
                  {TIPOS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* O canal só existe no telefone — o e-mail recebe o código por e-mail. */}
              {tipo === 'PHONE' ? (
                <div style={{ flex: '1 1 150px' }}>
                  <label style={{ fontSize: 12, fontWeight: 'bold', color: '#667085' }}>
                    Receber código por
                  </label>
                  <select
                    value={canal}
                    onChange={(e) => setCanal(e.target.value)}
                    className="w-full px-4 py-2 rounded"
                    style={{ color: '#101828', border: '1px solid #cccccc', marginTop: 4 }}
                  >
                    <option value="sms">SMS</option>
                    <option value="whatsapp">WhatsApp</option>
                  </select>
                </div>
              ) : null}
            </div>

            {/*
              O aviso da confirmação aparece ANTES de criar. Descobrir depois que ainda falta
              um código é o que fazia a pessoa achar que a chave já estava valendo.
            */}
            {PRECISA_CONFIRMAR.includes(tipo) ? (
              <p
                style={{
                  marginTop: 12,
                  fontSize: 12,
                  background: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: 6,
                  padding: 10,
                }}
              >
                Você vai receber um código por {rotuloContato} para confirmar. A chave só passa a
                valer depois disso.
              </p>
            ) : null}

            {erro ? (
              <p style={{ color: '#b42318', fontSize: 13, fontWeight: 'bold', marginTop: 12 }}>{erro}</p>
            ) : null}

            {/* Um botão só: criar é uma ação, não três. */}
            <button
              onClick={criar}
              disabled={!podeCriar}
              className="w-full px-4 py-2 bg-customGreen text-white rounded hover:bg-customGreenHover"
              style={{ marginTop: 16, opacity: podeCriar ? 1 : 0.6 }}
            >
              {enviando ? 'Criando...' : translations.create || 'Criar chave'}
            </button>

            <button
              onClick={onClose}
              disabled={enviando}
              className="w-full px-4 py-2 rounded"
              style={{ marginTop: 8, color: '#1c787e', background: 'transparent' }}
            >
              {translations.cancel || 'Cancelar'}
            </button>
          </>
        ) : (
          <>
            <h2 className="text-xl mb-1 text-center font-bold">Confirme a chave</h2>
            <p style={{ textAlign: 'center', fontSize: 12, color: '#667085', marginBottom: 16 }}>
              Enviamos um código por {rotuloContato}.
            </p>

            <label style={{ fontSize: 12, fontWeight: 'bold', color: '#667085' }}>Código</label>
            <input
              type="text"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="000000"
              className="w-full px-4 py-2 rounded"
              style={{ color: '#101828', border: '1px solid #cccccc', marginTop: 4, letterSpacing: 2 }}
            />
            <p style={{ fontSize: 11, color: '#667085', marginTop: 6 }}>
              Enquanto não for confirmada, a chave não existe no Banco Central.
            </p>

            {erro ? (
              <p style={{ color: '#b42318', fontSize: 13, fontWeight: 'bold', marginTop: 12 }}>{erro}</p>
            ) : null}

            <button
              onClick={confirmar}
              disabled={enviando || !codigo}
              className="w-full px-4 py-2 bg-customGreen text-white rounded hover:bg-customGreenHover"
              style={{ marginTop: 16, opacity: enviando || !codigo ? 0.6 : 1 }}
            >
              {enviando ? 'Confirmando...' : 'Confirmar'}
            </button>

            <button
              onClick={onClose}
              disabled={enviando}
              className="w-full px-4 py-2 rounded"
              style={{ marginTop: 8, color: '#1c787e', background: 'transparent' }}
            >
              {translations.cancel || 'Cancelar'}
            </button>
          </>
        )}
      </div>
      <ToastContainer />
    </ModalBase>
  );
}

export default Modal;
